import { GDPR_REGIONS, REGION_PARAM } from '../constants/regions';
import { API_V1 } from '../constants/api';
import { INTELLIGENCE_ARCHIVE_TAB } from '../constants/routes';

export function modifyUrlWithPrefix(url, prefix = null, prefixCondition = null) {
  const params = new URLSearchParams(window.location?.search);
  const context = params.get('context');

  if (prefix && prefixCondition) {
    // Handle both full URLs and relative paths
    let pathToCheck;

    try {
      // Try to parse as full URL
      const urlObj = new URL(url);
      pathToCheck = urlObj.pathname;
    } catch (e) {
      // If parsing fails, it's likely a relative path
      pathToCheck = url;
    }

    const indicatorUrl = `${API_V1}/indicator`;
    if (
      (url.includes(indicatorUrl) && window.location.pathname.includes(INTELLIGENCE_ARCHIVE_TAB)) ||
      (url.includes(indicatorUrl) && context === INTELLIGENCE_ARCHIVE_TAB)
    ) {
      return url.replace(indicatorUrl, `${API_V1}/global-indicator`);
    }

    // Skip region prefix for global-entity endpoints (used for ALL environment)
    if (pathToCheck.includes('/global-entity')) {
      return url;
    }

    if (pathToCheck.startsWith(prefixCondition)) {
      return url.replace(prefixCondition, `/${prefix}${prefixCondition}`);
    }
  }
  return url;
}

/**
 * Returns a normalized region string - or the default 'us' region if the input is not mapped or invalid.
 * @param {string} region - The region to normalize.
 * */
const GDPR_REGIONS_SET = new Set(Object.values(GDPR_REGIONS));

export const normalizeRegion = region => {
  const normalized = region?.toLowerCase();
  return GDPR_REGIONS_SET.has(normalized) ? normalized : GDPR_REGIONS.US;
};

// True only for recognized regions. Use before acting on an untrusted value
// (e.g. a URL param), unlike normalizeRegion which coerces junk to 'us'.
export const isGdprRegion = region => GDPR_REGIONS_SET.has(region?.toLowerCase());

// Build a query string portion for region (empty string if none or 'us')
export const buildRegionQuery = region => {
  if (!region) return '';
  const normalized = normalizeRegion(region);
  return normalized === GDPR_REGIONS.US ? '' : `${REGION_PARAM}=${normalized}`;
};

export const getRegion = () => {
  // Extract region from URL query parameters
  const params = new URLSearchParams(window.location?.search);
  const region = params.get(REGION_PARAM);
  if (region) return normalizeRegion(region);

  const regionFromSessionStorage = sessionStorage.getItem('selectedRegion');
  const parsedRegion = JSON.parse(regionFromSessionStorage)?.regionState?.toLowerCase();
  return normalizeRegion(parsedRegion);
};

/**
 * Maps each customer to its normalized region.
 * Previous implementation grouped customers by region (region => [customers]).
 * New implementation returns a flat object: { <customerId>: <region> } for O(1) lookups.
 * Falls back through several possible id fields: _id, id, customer_id, name.
 * Skips customers without any identifiable key.
 * @param {Array<object>} customers
 * @returns {Object<string,string>} customerId -> region
 */
export const mapCustomerToRegion = customers => {
  if (!Array.isArray(customers)) return {};
  return customers.reduce((acc, customer) => {
    if (!customer) return acc;
    const customerId = customer._id || customer.id || customer.customer_id || customer.name;
    if (!customerId) return acc;
    acc[customerId] = normalizeRegion(customer.region);
    return acc;
  }, {});
};
